/**
 * BoardCategoryManageModal — 게시판 카테고리 관리
 *
 * 카테고리 추가 / 인라인 이름 수정 / 삭제 / DnD 재정렬.
 * 진입점: LeftMenuBoard 헤더의 ⚙ 아이콘 (canManage 시에만).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Trans, t } from '@lingui/macro';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/use-toast';
import { AdminBadge } from '../components/AdminBadge';
import { Folders, GripVertical, Plus, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchBoardCategories, createBoardCategory, updateBoardCategory,
  deleteBoardCategory, reorderBoardCategories,
} from '../../services/BoardService';
import { BoardCategory } from '../../models/Board';

interface Props {
  open: boolean;
  onClose: () => void;
  workspaceId: string | null;
  /** 변경(추가/수정/삭제/정렬) 시 호출 — LeftMenuBoard 가 refresh 트리거 */
  onChanged?: () => void;
}

const BoardCategoryManageModal: React.FC<Props> = ({ open, onClose, workspaceId, onChanged }) => {
  const [categories, setCategories] = useState<BoardCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // 로드
  useEffect(() => {
    if (!open || !workspaceId) return;
    setLoading(true);
    fetchBoardCategories(workspaceId)
      .then(setCategories)
      .catch(err => {
        console.error('[CategoryManage] 로드 실패:', err);
        toast({ title: t`Failed to load categories`, variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [open, workspaceId]);

  const handleCreate = async () => {
    if (!workspaceId) return;
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await createBoardCategory(workspaceId, name);
      setCategories(prev => [...prev, created]);
      setNewName('');
      onChanged?.();
    } catch (err) {
      console.error('[CategoryManage] 추가 실패:', err);
      toast({ title: t`Failed to add category`, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (id: string, name: string) => {
    try {
      const updated = await updateBoardCategory(id, { name });
      setCategories(prev => prev.map(c => (c.id === id ? updated : c)));
      onChanged?.();
    } catch (err) {
      console.error('[CategoryManage] 이름 수정 실패:', err);
      toast({ title: t`Failed to rename`, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    const cat = categories.find(c => c.id === id);
    if (!cat) return;
    if (!window.confirm(t`Delete category "${cat.name}"?\nIts boards will be moved to "Uncategorized".`)) return;
    try {
      await deleteBoardCategory(id);
      setCategories(prev => prev.filter(c => c.id !== id));
      onChanged?.();
    } catch (err) {
      console.error('[CategoryManage] 삭제 실패:', err);
      toast({ title: t`Failed to delete category`, variant: 'destructive' });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = categories.findIndex(c => c.id === active.id);
    const newIdx = categories.findIndex(c => c.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(categories, oldIdx, newIdx);
    setCategories(reordered); // optimistic
    try {
      await reorderBoardCategories(reordered.map(c => c.id));
      onChanged?.();
    } catch (err) {
      console.error('[CategoryManage] 재정렬 실패:', err);
      toast({ title: t`Failed to reorder`, variant: 'destructive' });
      // rollback — 다시 fetch
      if (workspaceId) fetchBoardCategories(workspaceId).then(setCategories).catch(() => {});
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[520px] max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-5 py-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-body font-semibold text-foreground">
            <Folders className="size-[18px] text-primary" />
            <Trans>Manage categories</Trans>
            <AdminBadge variant="sm" />
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>Add, edit, delete and reorder categories used to group boards.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* 카테고리 list */}
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
              </div>
            ) : categories.length === 0 ? (
              <p className="text-meta-sm text-muted-foreground text-center py-8 leading-relaxed">
                <Trans>No categories yet.</Trans><br />
                <Trans>Add the first one from the input below.</Trans>
              </p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                  <ul className="space-y-1">
                    {categories.map(c => (
                      <CategoryRow
                        key={c.id}
                        category={c}
                        onRename={name => handleRename(c.id, name)}
                        onDelete={() => handleDelete(c.id)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* 새 카테고리 입력 */}
          <div className="pt-3 border-t border-border">
            <label className="block text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              <Trans>New category</Trans>
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
                placeholder={t`e.g. Announcements, Project, Resources…`}
                disabled={creating}
              />
              <Button
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                size="sm"
                className="shrink-0"
              >
                {creating ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5 mr-1" />}
                <Trans>Add</Trans>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── CategoryRow ──────────────────────────────────────────

const CategoryRow: React.FC<{
  category: BoardCategory;
  onRename: (name: string) => void;
  onDelete: () => void;
}> = ({ category, onRename, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(category.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(category.name);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, category.name]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const commit = () => {
    const next = draft.trim();
    if (!next || next === category.name) {
      setEditing(false);
      return;
    }
    onRename(next);
    setEditing(false);
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-2 rounded-lg border border-border bg-card"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="size-6 rounded-md flex items-center justify-center text-muted-foreground/60 hover:text-foreground hover:bg-muted cursor-grab active:cursor-grabbing transition-colors"
        title={t`Drag to reorder`}
        aria-label={t`Drag to reorder`}
      >
        <GripVertical className="size-3.5" />
      </button>

      {editing ? (
        <>
          <Input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditing(false);
            }}
            className="h-7 flex-1"
          />
          <button
            type="button"
            onClick={commit}
            className="size-6 rounded-md flex items-center justify-center text-primary hover:bg-primary/10 transition-colors"
            title={t`Save`}
          >
            <Check className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="size-6 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            title={t`Cancel`}
          >
            <X className="size-3.5" />
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 text-label text-foreground truncate">{category.name}</span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
            title={t`Rename`}
            aria-label={t`Rename`}
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            title={t`Delete`}
            aria-label={t`Delete`}
          >
            <Trash2 className="size-3.5" />
          </button>
        </>
      )}
    </li>
  );
};

export default BoardCategoryManageModal;
